import FormFieldInfoTooltip from '@atoms/FormFieldInfoTooltip/FormFieldInfoTooltip';
import Icon from '@atoms/Icon/Icon';
import { ILabelValue } from '@models/ILabelValue';
import classNames from 'classnames';
import { ErrorMessage, Field, useField } from 'formik';
import { DetailedHTMLProps, SelectHTMLAttributes } from 'react';

interface Props
  extends DetailedHTMLProps<
    SelectHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  readonly name: string;
  readonly options: ILabelValue[];
  readonly label?: string;
  readonly infoTooltip?: string;
  readonly optionalFieldLabel?: string;
  readonly placeholder?: string;
  readonly hideErrorMessage?: boolean;
  readonly disableErrorStyling?: boolean;
  readonly dataTestId?: string;
}

const FormSelect = ({
  label,
  infoTooltip,
  name,
  options,
  placeholder,
  required,
  disabled,
  optionalFieldLabel,
  hideErrorMessage = false,
  disableErrorStyling = false,
  dataTestId,
}: Props) => {
  const [, meta] = useField(name);

  return (
    <div className="flex flex-col space-y-1" data-testid={dataTestId}>
      {!!label && (
        <div className="flex justify-between">
          <label
            htmlFor={name}
            data-testid="form-select-label"
            className={classNames('body-four', {
              'text-red': meta.error && meta.touched && !disableErrorStyling,
            })}
          >
            {`${label}${required && !disabled ? ' *' : ''}`}
            {optionalFieldLabel && !required && !disabled ? (
              <>
                {' '}
                <span className="text-grey-600">{optionalFieldLabel}</span>
              </>
            ) : (
              ''
            )}
          </label>

          {infoTooltip && <FormFieldInfoTooltip infoTooltip={infoTooltip} />}
        </div>
      )}

      <div className="relative">
        <Field
          disabled={disabled}
          as="select"
          className={classNames(
            'body-three relative w-full appearance-none rounded-md bg-grey-100 px-5 py-4 opacity-100',
            {
              'ring-1 ring-red ring-inset':
                meta.error && meta.touched && !disableErrorStyling,
              'text-grey-600': disabled,
            },
            meta.value ? 'text-black-100' : 'text-grey-600'
          )}
          name={name}
          id={name}
          aria-label={name}
        >
          <>
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}

            {options.map(({ label: optionsLabel, value }) => (
              <option key={value} value={value}>
                {optionsLabel}
              </option>
            ))}
          </>
        </Field>

        {!disabled && (
          <div className="pointer-events-none absolute top-0 right-3 z-[1] flex h-full items-center">
            <Icon name="caret_down" />
          </div>
        )}
      </div>

      {meta.error && meta.touched && !hideErrorMessage && (
        <ErrorMessage name={name}>
          {(msg: string) => (
            <span
              data-testid="form-select-label"
              className="body-four text-red"
            >
              {msg}
            </span>
          )}
        </ErrorMessage>
      )}
    </div>
  );
};

export default FormSelect;
